import Styles from './styles.module.css';
import React, { useRef, useState } from 'react';
import { getFirestore, addDoc, collection, doc, updateDoc } from 'firebase/firestore';

export type SessionEditing = {
  id: string;
  name: string;
  description: string;
  schedule: { startAt: Date; durationMin: number };
  place: string;
  people: number;
};

type SessionMakeProps = {
  companyId: string;
  setMake: (open: boolean) => void;
  // 渡したときは、その説明会の編集
  editing?: SessionEditing | null;
};

// 日時の入力欄(datetime-local)に入れる形 'YYYY-MM-DDTHH:mm'(端末の時刻)
const toLocalInput = (d: Date | undefined): string => {
  if (!d || isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const SessionMake: React.FC<SessionMakeProps> = ({ companyId, setMake, editing = null }) => {

  const db = getFirestore();

  const bodyRef = useRef<HTMLDivElement | null>(null);

  const [ name, setName ] = useState<string>(editing?.name ?? '');
  const [ description, setDescription ] = useState<string>(editing?.description ?? '');
  const [ startAt, setStartAt ] = useState<string>(toLocalInput(editing?.schedule?.startAt));
  const [ durationMin, setDurationMin ] = useState<string>(editing?.schedule?.durationMin != null ? String(editing.schedule.durationMin) : '');
  const [ place, setPlace ] = useState<string>(editing?.place ?? '');
  const [ people, setPeople ] = useState<string>(editing?.people != null ? String(editing.people) : '');

  const [ errorName, setErrorName ] = useState(false);
  const [ errorStartAt, setErrorStartAt ] = useState(false);
  const [ errorDuration, setErrorDuration ] = useState(false);
  const [ errorPlace, setErrorPlace ] = useState(false);
  const [ errorPeople, setErrorPeople ] = useState(false);

  // 保存時のバリデーション結果を1件だけ上部に表示する
  const [ errorMessage, setErrorMessage ] = useState<string>('');

  // 保存中のロック。「追加」の連打で addDoc が複数回走り、別IDの重複ドキュメントが
  // 作られるのを防ぐ(state は反映が遅れるため、押した瞬間に効く ref で判定する)。
  const savingRef = useRef<boolean>(false);
  const [ saving, setSaving ] = useState<boolean>(false);

  const showError = (message: string) => {
    setErrorMessage(message);
    bodyRef.current?.scrollTo({ top: 0 });
  };

  async function save() {
    if (savingRef.current) return;

    setErrorName(false);
    setErrorStartAt(false);
    setErrorDuration(false);
    setErrorPlace(false);
    setErrorPeople(false);
    setErrorMessage('');

    if (name === '') {
      setErrorName(true);
      showError('説明会名を入力してください。');
    } else if (startAt === '') {
      setErrorStartAt(true);
      showError('開始日時を入力してください。');
    } else if (durationMin === '' || isNaN(Number(durationMin)) || Number(durationMin) <= 0) {
      setErrorDuration(true);
      showError('説明会の時間を分単位・半角数字で入力してください。');
    } else if (place === '') {
      setErrorPlace(true);
      showError('場所を入力してください。');
    } else if (people === '' || isNaN(Number(people)) || Number(people) <= 0) {
      setErrorPeople(true);
      showError('定員を半角数字で入力してください。');
    } else {
      savingRef.current = true;
      setSaving(true);
      try {
        const startDate = new Date(startAt);
        const endDate = new Date(startDate.getTime() + Number(durationMin) * 60 * 1000);

        if (editing) {
          await updateDoc(doc(db, 'infoSessions', editing.id), {
            name: name,
            description: description,
            schedule: {
              startAt: startDate,
              endAt: endDate,
              durationMin: Number(durationMin),
            },
            place: place,
            people: Number(people),
            updated_at: new Date(),
          });
          setMake(false);
          return;
        }

        await addDoc(collection(db, 'infoSessions'), {
          companyId: companyId,
          name: name,
          description: description,
          schedule: {
            startAt: startDate,
            endAt: endDate,
            durationMin: Number(durationMin),
          },
          place: place,
          people: Number(people),
          created_at: new Date(),
        })
        .then(() => setMake(false));
      } catch (error) {
        console.error('エラーが発生しました', error);
        showError(`説明会の${editing ? '保存' : '追加'}に失敗しました。時間をおいて、もう一度お試しください。`);
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    }

  }

  const inputClass = (hasError: boolean) =>
    hasError ? `${Styles.input} ${Styles.input_error}` : Styles.input;

  return (
    <div className={Styles.overlay} role='dialog' aria-modal='true'>
      <div className={Styles.scrim} onClick={() => setMake(false)} />
      <div className={Styles.card}>

        <div className={Styles.header}>
          <h3 className={Styles.headerTitle}>{editing ? '会社説明会の編集' : '会社説明会の追加'}</h3>
          <button type='button' className={Styles.headerBtn} disabled={saving} onClick={() => save()}>
            {saving ? (editing ? '保存中...' : '追加中...') : (editing ? '保存' : '追加')}
          </button>
        </div>

        <div className={Styles.body} ref={bodyRef}>

          {errorMessage && (
            <div className={Styles.errorBox} role='alert'>
              <p className={Styles.errorBox_label}>エラーメッセージ：</p>
              <p className={Styles.errorBox_text}>{errorMessage}</p>
            </div>
          )}

          {/* 説明会名 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>説明会名<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{name.length} / 30</span>
            </div>
            <input
              className={inputClass(errorName)}
              placeholder='〇〇 説明会'
              maxLength={30}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* 説明 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>説明<span className={Styles.optional}>任意</span></span>
              <span className={Styles.count}>{description.length} / 200</span>
            </div>
            <textarea
              className={`${Styles.input} ${Styles.textarea}`}
              placeholder='説明会の内容や持ち物などを入力してください'
              maxLength={200}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* 開始時刻 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>開始時刻<span className={Styles.req}>※</span></span>
            </div>
            <input
              className={inputClass(errorStartAt)}
              type='datetime-local'
              value={startAt}
              onChange={(e) => setStartAt(e.target.value)}
            />
          </div>

          {/* 説明会の時間（分） */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>説明会の時間<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{durationMin.length} / 3</span>
            </div>
            <input
              className={inputClass(errorDuration)}
              placeholder='60'
              maxLength={3}
              value={durationMin}
              onChange={(e) => setDurationMin(e.target.value)}
            />
          </div>

          {/* 場所 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>場所<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{place.length} / 50</span>
            </div>
            <input
              className={inputClass(errorPlace)}
              placeholder='東京都渋谷区...'
              maxLength={50}
              value={place}
              onChange={(e) => setPlace(e.target.value)}
            />
          </div>

          {/* 定員 */}
          <div className={Styles.field}>
            <div className={Styles.field_head}>
              <span className={Styles.label}>定員<span className={Styles.req}>※</span></span>
              <span className={Styles.count}>{people.length} / 3</span>
            </div>
            <input
              className={inputClass(errorPeople)}
              placeholder='10'
              maxLength={3}
              value={people}
              onChange={(e) => setPeople(e.target.value)}
            />
          </div>

        </div>
      </div>
    </div>
  );
}

export default SessionMake;
